import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Campaign, ReduxState } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { CAMPAIGN_STATUSES } from 'utils/models/Campaign';

type CampaignListApiResponse = {
  results: Campaign[];
};

const NO_CAMPAIGNS: Campaign[] = [];

// The campaigns of the current organization, most recent first, for roles
// scoped to a list of campaigns (e.g. CALIBRATION_VIEW_EDITOR). With
// activeOnly only the published ones are returned.
export const useOrganizationCampaigns = (
  enabled: boolean,
  { activeOnly = false }: { activeOnly?: boolean } = {}
): { campaigns: Campaign[]; isLoading: boolean } => {
  const organizationId = useSelector<ReduxState, number | undefined>(
    (state) => state.currentOrganization?.id
  );

  const params = useMemo(
    () => ({
      organization: organizationId,
      ...(activeOnly ? { status: CAMPAIGN_STATUSES.ACTIVE } : {}),
    }),
    [organizationId, activeOnly]
  );

  const { data, status } = useConfirmApi<CampaignListApiResponse>({
    url: '/campaigns',
    method: 'GET',
    params,
    disabled: !enabled || !organizationId,
  });

  return useMemo(
    () => ({
      campaigns: data?.results ?? NO_CAMPAIGNS,
      isLoading: enabled && status === 'LOADING',
    }),
    [data, enabled, status]
  );
};

export const getCampaignName = (
  campaignId: number,
  campaigns: Campaign[]
): string =>
  campaigns.find((campaign) => campaign.id === campaignId)?.name ??
  `#${campaignId}`;
